import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '@components/layout/Layout';
import {
  GetZipCityStateHandler,
  PlansAndPricesFormData,
  PlansAndPricesFormHandler,
  PlansAndPricesTemplate,
} from '@templates/misc/PlansAndPricesTemplate';
import { PlanPriceSummary } from '@components/card/CardPlanPriceSummary';
import ProfileContext from '@context/ProfileContext';
import { getProfileOfficesByType } from '@services/helpers/profile.offices.helper';
import { OfficeType } from '@app/models';
import {
  CityStates,
  DwellingType,
  ProductAvailabilityRequest,
  ProductAvailabilityResponse,
  ProductFilters,
  ProductPricingDetailsRequest,
  ProductPricingResponse,
} from '@apis/models';
import AddressApi from '@apis/address.api';
import { getBrand } from '@helpers/brand.utils';
import { DEFAULT_AGE } from '@services/helpers';
import ProductApi from '@apis/product.api';
import { ContentBox } from '@components/layout/ContentBox';
import msgs from '@app/locales/en';
import useGlobalAlert from '@app/core/GlobalAlertModal';
import ZestyApi from '@apis/zesty.apis';
import { PlansAndPricesMetaData, ZestyPlansAndPricesCoverage } from '@apis/models/zesty.api.model';
import { OptionalCoverageItem } from '@components/card/CardPlanPriceCoverage';
import { QuoteSummarySelection } from '@components/misc/QuoteSummary';
import { Canceler } from 'axios';
import {
  PNP_GUEST_UNIT_COVERAGE_ID,
  PNP_GUEST_UNIT_NAME_MATCH,
} from '@constants/plansAndPrices-constants';
import PlansAndPricesQueryParamsService from '../../services/queryParamParserService/plansAndPricesQueryParams.service';
import { stringHasValue } from '../../services/validation/ValidationRules';
import { FIRST_RESPONDER_DISCOUNT, MILITARY_DISCOUNT } from '@constants/newOrder-constants';
import { FEFeatures, useOptimizelyFE } from '@context/OptimizelyContext/fe-index';
import cloneDeep from 'lodash/cloneDeep';
import { getCurrentThemeConfig } from '@app/core/theme-config';
import getBrandConstants from '@constants/brands';

export function findMatchRecordByName<T, R>(data: T, search: string): R {
  const key =
    data &&
    Object.keys(data).find((key) =>
      search.toUpperCase().replace(' ', '').includes(key.toUpperCase().replace(' ', '')),
    );
  return key ? data[key] : null;
}

export const mapWYSIWYGBulletToArray = (htmlString: string): string[] => {
  const list: string[] = htmlString.split('<li>').map((str) => str.split('</li>')[0]);
  list.shift(); // remove first element, which is the first <ul> tag
  return list;
};

export const selectPlanByHighestPrice = (planData: PlanPriceSummary[]): PlanPriceSummary | null => {
  if (!planData || planData.length === 0) {
    return null;
  }
  return planData.reduce(
    (max, plan) => ((plan.price ?? 0) > (max.price ?? 0) ? plan : max),
    planData[0],
  );
};

const ProductApiCustom = ProductApi.new({
  suppressResponseErrorNotification: true,
  returnCancelErrorAsNull: false,
});

/** canceler used when form data has been changed */
let formDataCanceler: Canceler;
/** canceler used when plan selection has been changed */
let planSelectionCanceler: Canceler;
/** canceler used when quote data has been changed */
let quoteLoadCanceler: Canceler;

const PlansAndPrices: React.FC = () => {
  const { addErrorToQueue } = useGlobalAlert();

  const { profile } = useContext(ProfileContext);
  const brand = getBrand();
  const hasInitializedFormData = useRef(false);
  const isSelectingFromTemplate = useRef(false);

  const [plans, setPlans] = useState<PlanPriceSummary[]>([]);
  const [tradeServiceFee, setTradeServiceFee] = useState<number>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [initialFormData, setInitialFormData] = useState<PlansAndPricesFormData>(null);
  const [formData, setFormData] = useState<PlansAndPricesFormData>(null);
  const [zipState, setZipState] = useState<string>(null);

  const [loadingPageMeta, setLoadingPageMeta] = useState<boolean>(true);
  const [pageMeta, setPageMeta] = useState<PlansAndPricesMetaData>(null);
  const [coverageMeta, setCoverageMeta] = useState<ZestyPlansAndPricesCoverage[]>([]);
  const [loadingPriceData, setLoadingPriceData] = useState<boolean>(false);
  const [pricingData, setPricingData] = useState<ProductPricingResponse[]>([]);

  const [selectedPlan, setSelectedPlan] = useState<PlanPriceSummary>(null);
  const [selectedCoverageItems, setSelectedCoverageItems] = useState<OptionalCoverageItem[]>([]);

  const [productRequest, setProductRequest] = useState<ProductAvailabilityRequest>(null);
  const [availableProducts, setAvailableProducts] = useState<ProductAvailabilityResponse>(null);

  const [productPricingDetailsRequest, setProductPricingDetailsRequest] =
    useState<ProductPricingDetailsRequest>(null);
  const [isFetchingProduct, setIsFetchingProduct] = useState<boolean>(true);

  /** the list of coverages for the selected plan. */
  const [coveragesList, setCoveragesList] = useState<OptionalCoverageItem[]>([]);
  /** holds the selection used for the quote. */
  const [quoteSummary, setQuoteSummary] = useState<QuoteSummarySelection>(null);
  /** holds coverages for see all coverages */
  const [includedCoveragesMap, setIncludedCoveragesMap] = useState<{
    [pvid: string]: string[];
  }>({});
  const [cityOptions, setCityOptions] = useState<CityStates[]>([]);

  const [loadingFilters, setLoadingFilters] = useState<boolean>(false);
  const [planFilters, setPlanFilters] = useState<ProductFilters>(null);

  const feoptimizely = useOptimizelyFE();

  const [isPlanCoverageMFEEnabled, setIsPlanCoverageMFEEnabled] = useState(
    feoptimizely.isFeatureToggleOn(FEFeatures.PlanCoverageMFEEnabled),
  );

  const [loadingUpdatedCoveragesData, setLoadingUpdatedCoveragesData] = useState<boolean>(false);
  const [updatedCoveragesData, setUpdatedCoveragesData] = useState<any>(null);

  /** initialize page data */
  useEffect(() => {
    setLoadingPageMeta(true);
    ZestyApi.GetPlansAndPricesData(brand)
      .then((metaData) => {
        setPageMeta(metaData);
      })
      .catch((e) => {
        console.error('failed to load page meta', e);
      })
      .finally(() => {
        setLoadingPageMeta(false);
      });

    ZestyApi.GetPlansAndPricesCoverages(brand)
      .then((data) => {
        setCoverageMeta(data);
      })
      .catch((e) => {
        console.error('failed to load page meta', e);
      });
  }, []);

  useEffect(() => {
    if (plans.length > 0 && !loadingUpdatedCoveragesData && updatedCoveragesData === null) {
      setLoadingUpdatedCoveragesData(true);
      ZestyApi.GetUpdatedCoverages(getSelectedPlan()?.productPlanId, brand)
        .then((data) => {
          setUpdatedCoveragesData(data);
        })
        .catch((e) => {
          console.error('failed to load updated coverages data', e);
        })
        .finally(() => {
          setLoadingUpdatedCoveragesData(false);
        });
    }
  }, [plans]);

  /** unmount, clear up any cancel if any */
  useEffect(() => {
    return () => {
      formDataCanceler?.('unmounting');
      planSelectionCanceler?.('unmounting');
      quoteLoadCanceler?.('unmounting');
    };
  }, []);

  /** initialize the form data */
  useEffect(() => {
    // Only initialize once, even if profile changes multiple times (prevents infinite loop with HBW210)
    if (hasInitializedFormData.current) return;
    hasInitializedFormData.current = true;

    const initialFormData: PlansAndPricesFormData = {
      zipCode: '',
      includeSellersCoverage: false,
      residenceType: DwellingType.SingleFamilyResidence,
    };

    //check for query string params
    PlansAndPricesQueryParamsService.init();
    const paramInstance = PlansAndPricesQueryParamsService.data;
    initialFormData.residenceType = paramInstance.residenceType;
    initialFormData.zipCode = paramInstance.zipCode;
    initialFormData.includeSellersCoverage = paramInstance.includeSellersCoverage;

    //if the zip is invalid/empty, get it from profile. ResidenceType and sellersCoverage are already set to default values
    if (!stringHasValue(initialFormData.zipCode) && profile) {
      const profileREOffices = getProfileOfficesByType(profile, OfficeType.RealEstate);
      initialFormData.zipCode = profileREOffices?.[0]?.address?.zip;
    }

    setInitialFormData(initialFormData);
  }, [profile]);

  /* Update plan selection when metadata loads or pricing changes */
  useEffect(() => {
    // Skip if the selection came from template (service fee change)
    if (isSelectingFromTemplate.current) {
      isSelectingFromTemplate.current = false;
      return;
    }

    const plan = getSelectedPlan();
    if (coverageMeta && updatedCoveragesData && plan) {
      onPlanSelection(plan);
    }
  }, [coverageMeta, updatedCoveragesData, selectedPlan, pricingData]);

  /* if residence type or zip state was changed, we want to check restrictions again */
  useEffect(() => {
    setLoadingFilters(true);
    loadPlanFilters(formData?.residenceType, formData?.zipCode, zipState)
      .then(setPlanFilters)
      .finally(() => setLoadingFilters(false));
  }, [formData?.residenceType, zipState]);

  // Compute feature toggle once to avoid re-renders if feoptimizely reference changes
  useEffect(() => {
    const isEnabled = feoptimizely.isFeatureToggleOn(FEFeatures.PlanCoverageMFEEnabled);
    setIsPlanCoverageMFEEnabled(isEnabled);
  }, []);

  /** When the residence type changes, verify if the sellers coverage selection needs to change as well.
   *  If so, we want to disable the input and set the value to false. Otherwise, re-enable the input. */
  const disableSellersCoverageOption = useMemo(() => {
    const shouldEnableSellersCoverageOption = planFilters?.sellersCoverage;
    return !shouldEnableSellersCoverageOption;
  }, [planFilters]);

  const onSubmit: PlansAndPricesFormHandler = (v) => {
    setFormData(v);
    loadPlans(v);
  };

  const loadPlans = async (data: PlansAndPricesFormData): Promise<void> => {
    setLoading(true);
    setIsFetchingProduct(true);

    setPlans([]);
    setTradeServiceFee(null);

    // Attempt to fetch the state of the zip
    const { city, state: zipState, cityStates } = await getZipCityState(data.zipCode);
    setZipState(zipState);
    if (cityStates) {
      setCityOptions(cityStates);
    }
    // if not a valid zip or state for plans, then stop and return an empty list
    if (!isEligibleZipStateForPlans(data.zipCode, zipState)) {
      console.log('no plans for the ZIP/State', data.zipCode, zipState);
      setLoading(false);
      return;
    }

    if (city && data && zipState) {
      try {
        const {
          plans: newPlans,
          serviceFee,
          priceData: newPriceData,
          productRequest: newProductRequest,
          availableProducts: newAvailableProducts,
        } = await getPlansAndServiceFee(data, zipState, city);

        setPricingData(newPriceData);
        setPlans(newPlans);
        setProductRequest(newProductRequest);
        setAvailableProducts(newAvailableProducts);

        // Find matching plan: prioritize exact match (productPlanId/heading + serviceFee), then fallback
        const planToSelect =
          selectedPlan && newPlans.length > 0
            ? newPlans.find(
                (p) =>
                  (p.productPlanId === selectedPlan.productPlanId ||
                    p.heading === selectedPlan.heading) &&
                  p.serviceFee === selectedPlan.serviceFee,
              ) ||
              newPlans.find(
                (p) =>
                  p.productPlanId === selectedPlan.productPlanId ||
                  p.heading === selectedPlan.heading,
              ) ||
              selectPlanByHighestPrice(newPlans)
            : selectPlanByHighestPrice(newPlans);

        if (planToSelect) {
          setSelectedPlan(planToSelect);
          // Update coverages and quote summary if metadata is ready
          if (coverageMeta && updatedCoveragesData) {
            await onPlanSelection(
              planToSelect,
              data,
              newPriceData,
              newProductRequest,
              newAvailableProducts,
            );
          }
        }

        setTradeServiceFee(serviceFee);
        setLoading(false);
        setIsFetchingProduct(false);
      } catch (e) {
        if (ProductApiCustom.isCancellationError(e)) {
          console.debug('cancellation error', e);
          return;
        }
        console.error('failed to fetch plans', e);
        addErrorToQueue(msgs.PLAN_AND_PRICE_LOAD_FAILED);
        setLoading(false);
        setIsFetchingProduct(false);
      }
      return;
    }
  };

  const getZipCityState: GetZipCityStateHandler = async (
    zip: string,
  ): Promise<{
    city: string;
    state: string;
    cityStates: CityStates[];
  }> => {
    try {
      const zipDetails: any = await AddressApi.getZipDetails(zip, false);
      const zipData = zipDetails?.zipCodes?.[0];
      return {
        cityStates: zipDetails?.cityStates || null,
        city: zipData?.defaultCity || null,
        state: zipData?.stateAbbreviation || null,
      };
    } catch (e) {
      console.error('failed to fetch zip details', e);
      return null;
    }
  };

  /** checks if the provided zip and state should be allowed to fetch plans */
  const isEligibleZipStateForPlans = (_zip: string, _state: string): boolean => {
    // TODO: temporarily allow all zips. Replace with ARE-8569 and make this logic async
    return true;
  };

  const getPlansAndServiceFee = async (
    data: PlansAndPricesFormData,
    state: string,
    city: string,
  ): Promise<{
    plans: PlanPriceSummary[];
    serviceFee: number;
    priceData: ProductPricingResponse[];
    productRequest: ProductAvailabilityRequest;
    availableProducts: ProductAvailabilityResponse;
  }> => {
    // note, by default, ac coverage is true for non-ac states. this is just how it works
    const productRequest: ProductAvailabilityRequest = getProductAvailabilityRequest(
      data,
      state,
      city,
      true,
    );

    let mappedPlans: PlanPriceSummary[] = [];
    let serviceFee: number = 0;

    // make all the request calls and then start mapping the data
    formDataCanceler?.('fetching new plan data');
    const cancelSource = ProductApiCustom.createNewCancelTokenSource();
    formDataCanceler = cancelSource.cancel;
    const availableProducts = await ProductApiCustom.withRequestConfigAs({
      cancelToken: cancelSource.token,
    }).getProductAvailability(productRequest);
    const productsWithAC = availableProducts?.products || [];
    setProductRequest(productRequest);
    setAvailableProducts(availableProducts);
    // fetch prices for all products
    const priceData: ProductPricingResponse[] = await Promise.all(
      productsWithAC.map((product) =>
        ProductApiCustom.withRequestConfigAs({
          cancelToken: cancelSource.token,
        }).getProductPricingByProductId(product.starPVID, {
          property: productRequest?.property,
          options: {
            contractTermMonths: productRequest.options.contractTermMonths,
            listingTermInDays: product.listingTermInDays,
            earnixRuleID: null,
            initiatingOfficeID: productRequest?.office.id,
            initiatingOfficeFranchiseCode: null,
            specialDiscounts: null,
            buyerPaid: null,
          },
        }),
      ),
    );

    //
    // start mapping to plan price summary
    //
    mappedPlans = productsWithAC
      .map(
        (productWithAC) =>
          ({
            productPlanId: productWithAC.starProductPlanID,
            pvid: productWithAC.starPVID,
            heading: productWithAC.name,
            price: priceData.find((price) => price.product.id === productWithAC.starPVID)?.product
              .price,
            subtext: null, // loaded in the template
            items: null, // loaded in the template
            serviceFee: productWithAC.serviceFees,
            hasSellersCoverage: productWithAC.sellersCoverage,
          }) as PlanPriceSummary,
      )
      .sort((a, b) => a.price - b.price);

    // grab the service fee for the first plan in the list
    serviceFee = mappedPlans[0]?.serviceFee;

    if (serviceFee === 0) {
      console.warn('serviceFee is 0');
    }
    if (!mappedPlans.every((p) => p.serviceFee === serviceFee)) {
      console.warn(
        'serviceFee do not equal in amount',
        mappedPlans.map((p) => p.serviceFee),
      );
    }

    return { plans: mappedPlans, serviceFee, priceData, productRequest, availableProducts };
  };

  const getProductAvailabilityRequest = (
    data: PlansAndPricesFormData,
    state: string,
    city: string,
    includeACCoverage: boolean,
  ): ProductAvailabilityRequest => {
    return {
      office: {
        id: '111', // arbitrary office id to not get franchise products
      },
      property: {
        squareFootage: 4999,
        age: DEFAULT_AGE,
        residenceType: data.residenceType,
        address1: '111 plans and prices st', // arbitrary street address as it doesn't matter for this page
        address2: '',
        city,
        zip: data.zipCode,
        state,
      },
      options: {
        contractTermMonths: 12,
        contractListTimestamp: '',
        tenant: brand,
        sellersCoverage: data.includeSellersCoverage,
        acCoverage: includeACCoverage,
        roProductsOnly: false,
      },
    };
  };

  const loadPlanFilters = async (
    residenceType: DwellingType,
    zipCode: string,
    zipState: string,
  ): Promise<ProductFilters> => {
    if (residenceType && zipCode && zipState) {
      try {
        return await ProductApi.getFilters({
          age: DEFAULT_AGE,
          squareFootage: 4999,
          brand,
          franchiseCode: '',
          contractListTimestamp: '',
          roProductsOnly: false,
          typeOfResidence: residenceType,
          zip: zipCode,
          state: zipState,
        });
      } catch (e) {
        console.error('failed to load product filters', e);
      }
    }
    return null;
  };

  const getSelectedPlanPricingData = (pricing = pricingData, plan = selectedPlan) => {
    return plan && pricing.find((p) => p.product.id === plan.pvid);
  };

  const loadIncludedCoverages = (pvid: string): Promise<string[]> => {
    const storedCoverages = includedCoveragesMap[pvid];
    if (storedCoverages) {
      return new Promise((resolve) => {
        resolve(storedCoverages);
      });
    } else {
      return ProductApi.getProductCoveragesByProductId(pvid, formData.residenceType).then((res) => {
        const coverages = res.coverages.included
          .map((cvg) => cvg.name)
          .sort((a, b) => a.localeCompare(b));
        const test = includedCoveragesMap;
        test[pvid] = coverages;
        setIncludedCoveragesMap(test);
        return coverages;
      });
    }
  };

  const onPlanSelection = async (
    plan: PlanPriceSummary,
    data = formData,
    pricing = pricingData,
    reqData: ProductAvailabilityRequest = productRequest,
    availProds: ProductAvailabilityResponse = availableProducts,
  ) => {
    if (plan && coverageMeta && updatedCoveragesData) {
      // Mark that this selection is coming from external call (template)
      isSelectingFromTemplate.current = true;
      setSelectedPlan(plan);

      const productPricingData = getSelectedPlanPricingData(pricing, plan);

      planSelectionCanceler?.('fetching new plan coverage');
      const cancelSource = ProductApiCustom.createNewCancelTokenSource();
      planSelectionCanceler = cancelSource.cancel;
      const res = await ProductApiCustom.withRequestConfigAs({ cancelToken: cancelSource.token })
        .getProductCoveragesByProductId(plan.pvid, data.residenceType)
        .catch((err) => {
          if (ProductApiCustom.isCancellationError(err)) {
            return;
          }
          console.error('failed to get coverage data', err);
        });

      if (!res) {
        return;
      }

      // Map API coverages to UI format with pricing and metadata
      const availableCvgs: OptionalCoverageItem[] = res.coverages.optional
        .map<OptionalCoverageItem | null>((cvg) => {
          const price =
            productPricingData?.optionalCoverages.find((c) => c.id === cvg.starCoverageId)?.price ||
            0;

          if (!(isPlanCoverageMFEEnabled || getBrandConstants().plansPricesDisplayMFE)) {
            const zestyData = coverageMeta.find((c) => c.coverage_id === cvg.starCoverageId);
            return {
              id: cvg.starCoverageId,
              price,
              icon: zestyData?.optional_coverage_icon,
              name: zestyData?.updated_name || `<b>${cvg.name}</b>`,
              subtext: zestyData?.description || '<i>Not loaded</i>',
            };
          } else {
            const zestyData = updatedCoveragesData.find(
              (c) => c.productCoverageID === cvg.starCoverageId,
            );
            if (!zestyData) return null; // Skip if metadata not found

            return {
              id: cvg.starCoverageId,
              price,
              icon: '',
              name: zestyData.coverageName.toString().trimEnd(),
              subtext: '',
            };
          }
        })
        .filter((cvg): cvg is OptionalCoverageItem => cvg !== null)
        .sort((a, b) => {
          const zestyDataA = coverageMeta.find((c) => c.coverage_id === a.id);
          const zestyDataB = coverageMeta.find((c) => c.coverage_id === b.id);
          return zestyDataA?.display_sequence - zestyDataB?.display_sequence;
        });

      let nonHiddenAvailableCoverages: OptionalCoverageItem[];

      if (isPlanCoverageMFEEnabled || getBrandConstants().plansPricesDisplayMFE) {
        // When using MFE, don't filter by coverageMeta - use all available coverages
        nonHiddenAvailableCoverages = availableCvgs;
      } else {
        nonHiddenAvailableCoverages = availableCvgs.filter((cv) => {
          return coverageMeta.some((zestyData) => {
            return zestyData.coverage_id === cv.id && zestyData.ishidden === '0';
          });
        });
      }

      // Persist coverage selection if still exists
      let selectedItemsCopy = cloneDeep(selectedCoverageItems);
      selectedItemsCopy = selectedItemsCopy.filter(
        (c) => !!availableCvgs.find((availableCvg) => availableCvg.id === c.id),
      );

      await updateQuoteSummary(productPricingData, plan, selectedItemsCopy, reqData, availProds);
      setCoveragesList(nonHiddenAvailableCoverages);
      setSelectedCoverageItems(selectedItemsCopy);
    }
  };

  const updateQuoteSummary = async (
    priceData: ProductPricingResponse,
    selectedPlan: PlanPriceSummary,
    selectedCoverages: OptionalCoverageItem[],
    reqData: ProductAvailabilityRequest = productRequest,
    availProds: ProductAvailabilityResponse = availableProducts,
  ) => {
    if (!selectedPlan?.pvid || !priceData || !availProds?.products?.length) {
      return;
    }
    quoteLoadCanceler?.('fetching new quote summary');
    const cancelSource = ProductApiCustom.createNewCancelTokenSource();
    quoteLoadCanceler = cancelSource.cancel;
    setLoadingPriceData(true);

    const pricingDetailsRequest: ProductPricingDetailsRequest = {
      property: reqData?.property,
      options: {
        contractTermMonths: availProds?.products?.[0]?.contractTermMonths,
        listingTermInDays: availProds?.products?.[0]?.listingTermInDays,

        earnixRuleID: priceData.earnixRuleID,
        initiatingOfficeID: '',
        initiatingOfficeFranchiseCode: null,
        specialDiscounts: null,
        buyerPaid: null,
      },
      selected: {
        productID: selectedPlan.pvid,
        guestUnitQuantity: selectedCoverages.filter(
          (cvg) =>
            cvg.id === PNP_GUEST_UNIT_COVERAGE_ID ||
            cvg.name.toLowerCase().includes(PNP_GUEST_UNIT_NAME_MATCH),
        ).length
          ? 1
          : 0,
        optionalCoverages: selectedCoverages.map((cvg) => ({ id: cvg.id, quantity: 1 })),
        groupCoverages: null,
        // groupCoverages: selectedPlanCoverages.groupCoverages.map(cvg => ({ id: cvg.id, quantity: cvg.quantity })),
      },
    };

    const res = await ProductApiCustom.withRequestConfigAs({ cancelToken: cancelSource.token })
      .getProductPricingDetails(pricingDetailsRequest)
      .catch((err) => {
        if (ProductApiCustom.isCancellationError(err)) {
          return;
        }
        console.error('failed to get quote price', err);
      });

    if (!res) {
      return;
    }

    setProductPricingDetailsRequest(pricingDetailsRequest);
    setQuoteSummary({
      tax: {
        price: res.pricing.tax,
        rate: res.details.taxRate,
        taxableAmount: res.details.taxableAmount,
      },
      total: res.pricing.totalDueAtClosing,
      plan: {
        id: selectedPlan.pvid,
        name: selectedPlan.heading,
        price: selectedPlan.price,
      },
      coverages: selectedCoverages.map((c) => ({
        id: c.id,
        price: res.optionalCoverages.find((cov) => cov.id === c.id)?.price,
        name: c.name,
      })),
      sellersCoverage: {
        total: res.pricing.sellersCoverage,
        amount: res.details.dailyRate,
        days: res.details.listingDays,
      },
      hasSellersCoverage: selectedPlan.hasSellersCoverage,
      militaryDiscountApplied: res.appliedSpecialDiscounts.includes(MILITARY_DISCOUNT),
      responderDiscountApplied: res.appliedSpecialDiscounts.includes(FIRST_RESPONDER_DISCOUNT),
      discountAmount: res.pricing.appliedPromotion,
    });

    setLoadingPriceData(false);
  };

  const onCoverageSelection = async (cvg: OptionalCoverageItem) => {
    let selectedItemsCopy = cloneDeep(selectedCoverageItems);
    const matchingCoverage = selectedItemsCopy.find((coverage) => coverage.id === cvg.id);
    if (!matchingCoverage) {
      selectedItemsCopy.push(cvg);
    } else if (matchingCoverage) {
      selectedItemsCopy = selectedItemsCopy.filter((coverage) => coverage.id !== cvg.id);
    }

    await updateQuoteSummary(getSelectedPlanPricingData(), selectedPlan, selectedItemsCopy);
    setSelectedCoverageItems(selectedItemsCopy);
  };

  const onCoverageSelectionWithQuantity = async (cvg: OptionalCoverageItem, qty: number) => {
    let selectedItemsCopy = cloneDeep(selectedCoverageItems);
    const matchingCoverage = selectedItemsCopy.find((coverage) => coverage.id === cvg.id);
    if (!matchingCoverage && qty > 0) {
      selectedItemsCopy.push(cvg);
    } else if (matchingCoverage && qty === 0) {
      selectedItemsCopy = selectedItemsCopy.filter((coverage) => coverage.id !== cvg.id);
    }

    await updateQuoteSummary(getSelectedPlanPricingData(), selectedPlan, selectedItemsCopy);
    setSelectedCoverageItems(selectedItemsCopy);
  };

  const onServiceFeeChange = (newServiceFee: number) => {
    if (!selectedPlan || !plans.length) return;

    // Try to find the same plan type with new service fee, or fallback to first plan with that fee
    const planToSelect =
      plans.find(
        (plan) =>
          plan.productPlanId === selectedPlan.productPlanId && plan.serviceFee === newServiceFee,
      ) || plans.find((plan) => plan.serviceFee === newServiceFee);

    if (planToSelect) {
      isSelectingFromTemplate.current = true;
      onPlanSelection(planToSelect);
    } else {
      console.warn(`No plan available with serviceFee=${newServiceFee}`);
    }
  };

  const getSelectedPlan = () => {
    /** Return selectedPlan or find matching plan from plans array */
    if (selectedPlan) {
      const plan = plans.find((plan) => {
        return plan.pvid === selectedPlan?.pvid || plan.heading === selectedPlan?.heading;
      });
      return plan || selectedPlan;
    }

    return plans.length > 0
      ? selectPlanByHighestPrice(plans)
      : null;
  };

  return (
    <Layout isLoggedIn={true} slug="plans-and-prices">
      <ContentBox
        title="Plans & Prices"
        subtitleHtml={pageMeta?.page_header_subtext}
        applyContainerWidthToHeaderOnly={getCurrentThemeConfig().plansPricesContentBoxWidth}
      >
        <PlansAndPricesTemplate
          loadingPageMeta={!!loadingPageMeta && !!pageMeta}
          loading={loading || isFetchingProduct}
          loadingPrice={loadingPriceData}
          initialFormData={initialFormData}
          formData={formData}
          cityOptions={cityOptions}
          plans={plans}
          tradeServiceFee={tradeServiceFee}
          onSubmit={onSubmit}
          getZipCityState={getZipCityState}
          meta={pageMeta}
          autoSubmitForm={true}
          onPlanSelection={onPlanSelection}
          onServiceFeeChange={onServiceFeeChange}
          onCoverageSelection={onCoverageSelection}
          onCoverageSelectionWithQuantity={onCoverageSelectionWithQuantity}
          setSelectedCoverageItems={setSelectedCoverageItems}
          selectedPlan={getSelectedPlan()}
          setSelectedPlan={setSelectedPlan}
          optionalCoverages={coveragesList}
          selectedCoverages={selectedCoverageItems}
          quoteSummary={quoteSummary}
          productPricingDetailsRequest={productPricingDetailsRequest}
          getIncludedCoverages={loadIncludedCoverages}
          loadingFilters={loadingFilters}
          disableSellersCoverageOption={disableSellersCoverageOption}
        />
      </ContentBox>
    </Layout>
  );
};

export default PlansAndPrices;
